import { useMemo } from "react";
import {
  BuildingIcon,
  LayoutGridIcon,
  LinkIcon,
  TextAlignStartIcon,
  UsersIcon,
} from "lucide-react";

import { ReportsViewWidgetSource } from "@/app/app/_/views/ReportsView/ReportsViewWidgetSource/ReportsViewWidgetSource";
import {
  getSelectedViewId,
  normalizeReportDefinition,
  widgetDisplayTitle,
} from "@/app/app/_/views/ReportsView/ReportsView.utils";
import { useReportSourceCatalog } from "@/app/app/_/views/ReportsView/reportFilterDefinitions.hooks";
import { ReportsViewWidgetPresentationPreview } from "@/app/app/_/views/ReportsView/ReportsViewWidget/ReportsViewWidgetPairingPicker";
import {
  BAR_COLOR_ORDER,
  LINE_COLOR_ORDER,
  PIE_COLOR_ORDER,
} from "@/components/organisms/Chart";
import { SimpleEnum } from "@/components/molecules/SimpleEnum";
import { EnumColors } from "@/constants/values";
import {
  getCardGridStyle,
  resolveDesktopLayoutCards,
  resolveReportsDashboardVisibleCards,
} from "@/app/app/_/views/ReportsView/reportsGridLayout";
import { ViewLayoutFragment } from "@/__generated__/graphql";
import { ObjectCardProps, ObjectType } from "@/objects/@types";
import { getReferenceValueOptionsByTypename } from "@/objects";
import { State } from "@/state";
import { Utils } from "@/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";

/** What each kind of chart is drawn in, where it is drawn in anything. */
const PREVIEW_PALETTES: Record<string, readonly string[] | undefined> = {
  BAR_CHART: BAR_COLOR_ORDER,
  PIE_CHART: PIE_COLOR_ORDER,
  LINE_CHART: LINE_COLOR_ORDER,
};

/**
 * The report itself, in miniature.
 *
 * A list of reports is a list of names and three lines of grey, and one report
 * looks like the next. The page each card opens is already on the card - the
 * definition comes down with it - so the card draws it: the same squares in
 * the same places, each wearing the mark of what is drawn there. Nothing is
 * fetched for this.
 */
function ReportCardMiniature({
  definition,
}: {
  definition: ViewLayoutFragment["definition"];
}) {
  const catalogue = useReportSourceCatalog();
  const { cards, widgetsById, tabs } = useMemo(() => {
    const report = normalizeReportDefinition(definition);
    // A report with tabs opens on its first one, and that is the page the
    // miniature draws; the rest are named above it, as they are on the report.
    const groups = report.groups ?? [];
    const layouts = groups[0]?.layouts ?? report.layouts;
    return {
      cards: resolveReportsDashboardVisibleCards(
        resolveDesktopLayoutCards(layouts),
        report.widgets,
        "grid",
      ),
      widgetsById: new Map(report.widgets.map((widget) => [widget.id, widget])),
      tabs: groups.length > 1 ? groups.map((group) => group.name) : [],
    };
  }, [definition]);

  if (cards.length === 0) return null;

  return (
    <div
      data-testid="report-card-miniature"
      className={cn(
        // The picture takes what the card has left rather than a fixed strip
        // of it, so a report with two widgets and one with eight both fill it.
        "mt-2 flex-1 min-h-0 flex flex-col gap-1 w-full",
        // At rest the whole picture is grey, whatever each drawing is written
        // in, and it holds still. Under the pointer the charts come up in
        // their own colours and start moving - a list of reports reads as
        // paper until you look at one.
        "[&_.report-miniature-ink]:[filter:grayscale(1)] [&_.report-miniature-ink]:opacity-80 [&_.report-miniature-ink]:[transition:filter_0.15s_ease,opacity_0.15s_ease]",
        "[&:hover_.report-miniature-ink]:[filter:none] [&:hover_.report-miniature-ink]:opacity-100 [&:hover_.report-miniature-ink_*]:[animation-play-state:running]",
      )}
    >
      {tabs.length > 0 && (
        <div
          data-testid="report-card-miniature-tabs"
          className="flex gap-0.5 min-w-0 overflow-hidden"
        >
          {tabs.map((name, index) => (
            <Typography
              key={name}
              level="body-xs"
              // The one the report opens on wears the surface, as it does
              // in the tab strip above the real page.
              className={cn(
                "truncate",
                "pl-1 pr-1 pt-px pb-px rounded text-[0.5625rem] [line-height:1.3]",
                index === 0 ? "shrink-0" : "shrink",
                "min-w-0",
                index === 0
                  ? "text-muted-foreground bg-card [border-width:1px] [border-style:solid] border-border"
                  : "text-muted-foreground/80",
              )}
            >
              {name}
            </Typography>
          ))}
        </div>
      )}
      <div
        data-testid="report-card-miniature-grid"
        className="flex-1 min-h-0 grid grid-cols-4 auto-rows-[minmax(62px,1fr)] gap-1 w-full"
      >
        {cards.map((card, index) => {
          const widget = widgetsById.get(card.cardId);
          // A chart is drawn in the colours a chart of that kind is drawn in on
          // the report itself - bars run through the series colours, a pie gets
          // its two slices. A figure and a table carry no series, so they are
          // written in the page's own grey and stay that way.
          const palette = PREVIEW_PALETTES[widget?.presentation ?? ""];

          return (
            <div
              key={card.cardId}
              className="flex flex-col gap-0.5 pl-1.5 pr-1.5 pt-1 pb-1 min-w-0 overflow-hidden rounded-sm text-muted-foreground/80 bg-card border border-solid border-border"
              style={getCardGridStyle(card, "grid")}
            >
              {/* Named at the head of the tile, where the widget it stands for
                carries its own title. */}
              {widget && (
                <Typography
                  level="body-xs"
                  className="truncate max-w-full text-[0.625rem] [line-height:1.2] text-muted-foreground"
                >
                  {widgetDisplayTitle(widget, catalogue)}
                </Typography>
              )}
              <div className="flex-1 min-h-0 flex items-center justify-center pl-0.5 pr-0.5">
                <div className="report-miniature-ink w-full flex justify-center">
                  <ReportsViewWidgetPresentationPreview
                    presentation={widget?.presentation}
                    scale="compact"
                    palette={palette}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ViewCard({
  data,
  actions: _actions,
  isLoading = false,
}: ObjectCardProps<ViewLayoutFragment>) {
  // Only the entities its reader is a member of: the server leaves out any
  // other the report reads, so this counts what they can see of it.
  const entityCount = data?.entities.length;
  const updatedAt = data?.updatedAt ?? data?.createdAt;
  // One line rather than three stacked ones, so the picture below it has the
  // card to itself. Whose it is, is the box beside the title.
  const said = [
    entityCount
      ? `${entityCount} entit${entityCount === 1 ? "y" : "ies"}`
      : null,
    updatedAt ? `Updated ${Utils.fTimeAgo(new Date(updatedAt))}` : null,
  ].filter(Boolean);

  return (
    <div className="flex flex-col gap-1.5 h-full min-h-0">
      <Typography level="body-xs" className="text-muted-foreground/80">
        <Skeleton loading={isLoading} variant="inline">
          {said.join(" · ")}
        </Skeleton>
      </Typography>

      {/* The picture takes what the card has left, standing in for one too. */}
      <Skeleton
        loading={isLoading}
        variant="rectangular"
        className="w-full max-w-full flex [flex-direction:inherit] [flex-wrap:inherit] [align-items:inherit] [justify-content:inherit] gap-[inherit] flex-1 min-h-0"
      >
        <ReportCardMiniature definition={data?.definition} />
      </Skeleton>

      <div className="mt-auto flex gap-1 flex-wrap">
        {data?.linkSharingEnabled && (
          <Skeleton
            loading={isLoading}
            variant="rectangular"
            className="w-fit max-w-full flex [flex-direction:inherit] [flex-wrap:inherit] [align-items:inherit] [justify-content:inherit] gap-[inherit]"
          >
            <SimpleEnum
              label="Public link"
              enumColor={EnumColors.BLUE}
              size="sm"
            />
          </Skeleton>
        )}
      </div>
    </div>
  );
}

const object: ObjectType<ViewLayoutFragment> = {
  humanName: "report",
  typename: "ReportObject",
  inputKey: "report",
  ReactIcon: LayoutGridIcon,
  isEntity: false,
  titleFieldKey: "name",
  Card: ViewCard,
  placeholder: {
    __typename: "ReportObject",
    id: btoa("ReportObject:placeholder"),
    identifier: "placeholder",
    name: "Monthly overview",
    entities: [
      {
        __typename: "EntityObject",
        id: btoa("EntityObject:placeholder"),
        identifier: "placeholder",
        name: "Elm Street Coffee",
      },
    ],
    updatedAt: "2026-01-15T10:00:00Z",
    linkSharingEnabled: false,
    // Four half-width charts, two rows of two: what a report usually is.
    definition: {
      version: 3,
      layouts: Object.fromEntries(
        ["xs", "sm", "md", "lg", "xl"].map((size) => [
          size,
          {
            cards: ["a", "b", "c", "d"].map((cardId, index) => ({
              cardId,
              colSpan: 2,
              visible: true,
              colStart: index % 2 === 0 ? 1 : 3,
              colEnd: index % 2 === 0 ? 3 : 5,
              rowStart: index < 2 ? 1 : 2,
              rowEnd: index < 2 ? 2 : 3,
            })),
          },
        ]),
      ),
      widgets: [
        { id: "a", source: "overview_kpis", presentation: "KPI_STRIP" },
        { id: "b", source: "money_in_out", presentation: "BAR_CHART" },
        { id: "c", source: "transactions", presentation: "PIE_CHART" },
        { id: "d", source: "bank_accounts", presentation: "GROUPED_SUMMARY" },
      ],
    },
    entity: {
      id: btoa("EntityObject:placeholder"),
      identifier: "placeholder",
      name: "Elm Street Coffee",
    },
  } as unknown as ViewLayoutFragment,
  getTitle: (data) => data?.name,
  // The entity it belongs to, drawn beside its title: its members have it.
  getEntityId: (data) => data?.entity?.id ?? null,
  groups: [
    {
      id: "sources",
      label: "Add charts",
      // The catalogue is a drag source for the report canvas, so it is only
      // worth showing while that canvas is on screen. Opened from the reports
      // list the panel is just the record, and there is nowhere to drag a
      // chart to.
      isVisible: () => {
        const { activeTabNodeIds, activeTab } = State.getState();
        return Boolean(getSelectedViewId(activeTabNodeIds, activeTab));
      },
      component: ReportsViewWidgetSource,
      flush: true,
    },
    {
      id: "overview",
      label: "Sharing",
    },
  ],
  fieldDefinitions: [
    {
      objectKey: "name",
      label: "Name",
      type: "text",
      ReactIcon: TextAlignStartIcon,
      asString: (data) => data?.name ?? "",
      getValue: (data) => data?.name ?? "",
      getValueOptions: () => ({ placeholder: "Report name" }),
      updateProps: {
        isEditable: true,
        isRequired: true,
      },
      createProps: {
        isRequired: true,
      },
      summaryProps: {
        isVisible: true,
      },
      fieldProps: {
        headerFieldType: "string",
        isSortable: true,
        isFilterable: true,
        isResizable: true,
      },
    },
    {
      // What it reads: said as it is made, and changed after by its owner. A
      // reader sees only the businesses of it they are in.
      objectKey: "entities",
      inputKey: "entityIdentifiers",
      label: "Reports on",
      type: "references",
      ReactIcon: BuildingIcon,
      asString: (data) =>
        (data?.entities ?? []).map((entity) => entity.name).join(", "),
      getValue: (data) =>
        (data?.entities ?? []).map((entity) => ({
          __typename: "EntityObject" as const,
          id: entity.id,
          identifier: entity.identifier,
          name: entity.name,
        })),
      getValueForApi: (data: ViewLayoutFragment) =>
        (data?.entities ?? []).map((entity) => entity.identifier),
      getValueOptions: () =>
        getReferenceValueOptionsByTypename("EntityObject")([]),
      createProps: {
        isRequired: true,
      },
      updateProps: {
        isEditable: true,
        isRequired: true,
      },
      summaryProps: {
        isVisible: true,
      },
    },
    {
      // Every member of this entity has the report; putting it in a business
      // shares it with the business. Only its owner moves it.
      objectKey: "entity",
      inputKey: "entity",
      groupId: "overview",
      label: "Belongs to",
      type: "reference",
      ReactIcon: BuildingIcon,
      asString: (data) => data?.entity?.name ?? "",
      getValue: (data) => ({
        __typename: "EntityObject",
        name: data?.entity?.name ?? null,
        id: data?.entity?.id ?? null,
        identifier: data?.entity?.identifier ?? null,
      }),
      getValueOptions: () =>
        getReferenceValueOptionsByTypename("EntityObject")([]),
      updateProps: {
        isEditable: true,
        isRequired: true,
      },
      summaryProps: {
        isVisible: true,
      },
    },
    {
      objectKey: "linkSharingEnabled",
      groupId: "overview",
      label: "Is public",
      type: "boolean",
      ReactIcon: LinkIcon,
      // ``shareToken`` is server-generated when link sharing is enabled. The
      // link row reads it via ``linkedValuesKeys`` too, but that row is hidden
      // until both flags are set — so we must track the token here or the panel
      // store never picks it up from the mutation response and the link row
      // never appears.
      linkedValuesKeys: ["linkSharingEnabled", "shareToken"],
      asString: (data) => (data?.linkSharingEnabled ? "Yes" : "No"),
      getValue: (data) => Boolean(data?.linkSharingEnabled),
      getValueForApi: (data: ViewLayoutFragment) =>
        data?.linkSharingEnabled
          ? { enableLinkSharing: true }
          : { disableLinkSharing: true },
      updateProps: {
        isEditable: true,
        isRequired: false,
      },
      summaryProps: {
        isVisible: true,
      },
      fieldProps: {
        headerFieldType: "string",
        isSortable: false,
        isFilterable: false,
        isResizable: true,
      },
    },
    {
      objectKey: "link",
      groupId: "overview",
      label: "Link",
      type: "url",
      ReactIcon: LinkIcon,
      asString: (data) =>
        data?.shareToken ? `/share/report/${data.shareToken}` : "",
      getValue: (data) => {
        if (!data?.linkSharingEnabled || !data.shareToken) {
          return { url: "", label: "" };
        }
        return {
          url: `${window.location.origin}/share/report/${data.shareToken}`,
          label: `/share/report/${data.shareToken}`,
        };
      },
      linkedValuesKeys: ["linkSharingEnabled", "shareToken"],
      updateProps: {
        isEditable: false,
        isVisible: (data) =>
          Boolean(data?.linkSharingEnabled && data?.shareToken),
      },
      summaryProps: {
        isVisible: (data) =>
          Boolean(data?.linkSharingEnabled && data?.shareToken),
      },
      fieldProps: {
        // A share link the app builds from the report's secret token: not written.
        exportPath: null,
        headerFieldType: "url",
        isSortable: false,
        isFilterable: false,
        isResizable: false,
      },
    },
    {
      objectKey: "shareContacts",
      inputKey: "shareEmails",
      groupId: "overview",
      label: "Contacts to share with",
      type: "references",
      ReactIcon: UsersIcon,
      asString: (data) =>
        (data?.shareContacts ?? [])
          .map((contact) => contact.emailAddress || contact.name)
          .filter(Boolean)
          .join(", "),
      getValue: (data) =>
        (data?.shareContacts ?? []).map((contact) => ({
          __typename: "ContactObject" as const,
          id: contact.id,
          identifier: contact.identifier,
          name: contact.name ?? contact.emailAddress ?? contact.identifier,
          emailAddress: contact.emailAddress,
        })),
      getValueForApi: (data: ViewLayoutFragment) =>
        (data?.shareContacts ?? [])
          .map((contact) => contact?.emailAddress?.trim())
          .filter((email): email is string => Boolean(email)),
      getValueOptions: (data) =>
        getReferenceValueOptionsByTypename("ContactObject")(
          data?.entities?.length
            ? data.entities.map((entity) => entity.identifier)
            : undefined,
        ),
      updateProps: {
        isEditable: true,
        isRequired: false,
      },
      summaryProps: {
        isVisible: true,
      },
      fieldProps: {
        headerFieldType: "references",
        isSortable: false,
        isFilterable: false,
        isResizable: true,
      },
    },
  ],
};

export { object as ReportObject };
